/**
 * THE OWNERS' CHECKS MARK, ON THE SCREENS (ops #85, the business's decision of 2026-10-22,
 * `docs/plan-call-off.md` section 3, decision 1).
 *
 * Every door that chooses a cleaner for a client's visit refuses one without the mark, and the server
 * is what says so (`api/src/schedule/checks.ts`); nothing here enforces anything. What this decides is
 * what a picker offers, so nobody is offered a choice the server refuses, and the plain words for a
 * cleaner placed before the rule who is still on a client's visit, so the office can fix it. Facts,
 * never a badge on a person.
 */
import type { ChecksOfCleaner, CleanerRow } from './api.ts';

/**
 * Whom a form offers for a client's visit: active and marked, or the cleaner the schedule already
 * has, since keeping them is no new choice and dropping them from the list would empty the field.
 */
export function offeredForAClient(cleaner: CleanerRow, carried?: string | null): boolean {
  if (cleaner.status !== 'active') return false;
  return Boolean(cleaner.checksCompleteOn) || (carried != null && cleaner.id === carried);
}

/** The one line on the crew list when an active cleaner has no mark yet. */
export function crewChecksNote(rows: readonly CleanerRow[]): string | null {
  return rows.some((row) => row.status === 'active' && !row.checksCompleteOn)
    ? "Some active cleaners have no checks mark yet, so they cannot be given a client's visit until an owner sets it on their card."
    : null;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** What a cleaner's card says beside "Checks not complete", or null with the mark set. */
export function unmarkedWorkWords(checks: ChecksOfCleaner | undefined): string | null {
  if (!checks || checks.complete) return null;
  const first = "Cannot be given a client's visit until an owner sets the mark.";
  if (checks.liveSeries === 0 && checks.upcomingVisits === 0) return first;
  return `${first} Still on ${plural(checks.liveSeries, 'standing schedule', 'standing schedules')} and ${plural(checks.upcomingVisits, 'visit to come', 'visits to come')} from before: an owner sets the mark, or the office gives them to a marked cleaner.`;
}

/** The warning on a visit to come with an unmarked cleaner on it, or null with none. */
export function unmarkedOnVisitWords(names: readonly string[] | undefined): string | null {
  if (!names || names.length === 0) return null;
  const who =
    names.length === 1
      ? `${names[0]} has`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} have`;
  return `${who} not been marked as having completed the checks. An owner sets the mark on the Crew screen, or the office gives this clean to a marked cleaner.`;
}
